import React, { FC, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { StepComment } from "./StepComment";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, IconButton, Tooltip, useTheme } from "@mui/material";

import { tokens } from "@/components/Application/theme";
import { useFetchChecklist } from "@/components/Procedures/API/ChecklistQueries";
import { StepContent } from "@/components/Procedures/Checklist/Step/StepContent";
import { StepLog } from "@/components/Procedures/Checklist/Step/StepLog";
import { StepMeasurements } from "@/components/Procedures/Checklist/Step/StepMeasurement";
import { StepTools } from "@/components/Procedures/Checklist/Step/StepTools";
import {
	getColumnTypeClass,
	getRowTypeClass,
	getTextColorForBackground,
} from "@/components/Procedures/Checklist/utils/Helpers";
import useValueWithBoundaries from "@/components/Procedures/Checklist/utils/useValueWithBoundaries";
import { ProcedureUpdateSuggestion } from "@/components/Procedures/ProcedureUpdateSuggestions";
import { CheckpointType } from "@/components/Procedures/Types/ManualChecklistTypes";

import { AlertRequest } from "@/helpers/alerts/AlertRequest";
import Ribbon from "@/helpers/components/Ribbon";

import { ProcedureNodeStep } from "@/model/Procedure/ProcedureNodeStep";
import { UpdateProcedureDetailStepStatusRequest } from "@/model/Procedure/UpdateProcedureDetailStepStatusRequest";

import { UpdateProcedureDetailStepState } from "@/service/ProcedureMutations";
import { RouteParams } from "@/service/Utilities";

interface StepProps {
	activeSchemeId: number | undefined;
	step: ProcedureNodeStep;
	materialHandling?: boolean;
	showCommentsAndSuggestion?: boolean | undefined;
	highlightedStepId?: number | null;
	/** id of the scrollable container that should be scrolled (panel) */
	scrollContainerId?: string;
}

export const Step: FC<StepProps> = (props) => {
	const {
		step,
		activeSchemeId,
		highlightedStepId,
		showCommentsAndSuggestion,
		scrollContainerId,
	} = props;

	const { id, schemeId } = useParams<RouteParams>();
	const checklistId = Number(schemeId);
	const theme = useTheme();
	const colors = tokens(theme.palette);
	const [autoMode, setAutoMode] = useState(false);
	const [showMeasurements, setShowMeasurements] = useState(false);
	const [showStepLog, setShowStepLog] = useState(false);
	const [showAllItems, setShowAllItems] = useState(false);
	const updateStepState = UpdateProcedureDetailStepState();

	const { data: checklist } = useFetchChecklist(checklistId);

	const toggleShowMeasurements = () => {
		setShowMeasurements((v) => !v);
		setAutoMode(false);
	};

	const { stepStatuses, values } = useValueWithBoundaries(
		step,
		Number(activeSchemeId) || 0,
	);

	const toggleShowStepLog = () => setShowStepLog((log) => !log);
	const toggleShowAllItems = () => setShowAllItems((v) => !v);

	const handleSkipStep = async () => {
		const request: UpdateProcedureDetailStepStatusRequest = {
			ArchiveType: "Status",
			StepId: step.Id,
			ProcedureId: Number(id),
			SchemeId: activeSchemeId ?? 0,
			IsSkipped: !step.IsSkipped,
			Status: 0,
			Metadata: { ...step, Status: 0 },
		};
		try {
			await updateStepState(request);
		} catch (e) {
			AlertRequest.failed();
			console.error(e);
		}
	};

	const isEligibleForMoreItems =
		step.ProcedureUpdateSuggestions.filter((s) => s.Status !== "Declined")
			.length >= 2 || step.Comments.length >= 2;

	const isHighlighted = step.Id === highlightedStepId;

	const rowStyle =
		step.CheckpointType === CheckpointType.Info
			? {
					backgroundColor: (step.BackgroundColor || "white") + " !important",
					borderWidth: ".5px 0 .5px 0",
					borderStyle: "dotted",
					borderColor: colors.grey[100],
				}
			: {};

	const stepRef = useRef<HTMLDivElement | null>(null);

	const measurementsVisible =
		step?.ChecklistType === "Listener" ||
		step?.ChecklistType === "ManualMeasurement";

	// ✅ Scroll only the provided container, not the window
	useEffect(() => {
		if (!isHighlighted || !stepRef.current) return;

		const container = scrollContainerId
			? document.getElementById(scrollContainerId)
			: null;

		if (container) {
			const cRect = container.getBoundingClientRect();
			const eRect = stepRef.current.getBoundingClientRect();
			// distance from container top to element top
			const deltaTop = eRect.top - cRect.top;
			// center the element in the container
			const offset = deltaTop - (container.clientHeight / 2 - eRect.height / 2);

			container.scrollTo({
				top: container.scrollTop + offset,
				behavior: "smooth",
			});
		} else {
			// fallback (no container id provided)
			stepRef.current.scrollIntoView({
				behavior: "smooth",
				block: "center",
				inline: "nearest",
			});
		}
	}, [isHighlighted, scrollContainerId]);

	return (
		<>
			<Box
				className={getRowTypeClass(step)}
				id={String(step.Id)}
				sx={{
					...rowStyle,
					position: "relative",
					borderRadius: 2,
					// keep the step’s own background / text colors
					backgroundColor: step.BackgroundColor
						? `${step.BackgroundColor} !important`
						: "",
					color: step.BackgroundColor
						? getTextColorForBackground(step.BackgroundColor)
						: "black",

					// smooth feel
					transition:
						"box-shadow .18s ease, transform .12s ease, background-color .18s ease",

					// subtle default
					boxShadow: "inset 0 0 0 1px rgba(0,0,0,.06)",

					...(isHighlighted && {
						// soft focus ring + outer glow
						boxShadow: (t) => `
      inset 0 0 0 2px ${t.palette.info.main},
      0 6px 20px ${t.palette.mode === "light" ? "rgba(0, 133, 255, .18)" : "rgba(0, 133, 255, .28)"}
    `,
						// a hair of lift
						transform: "translateY(-1px)",
						// subtle tint if no custom bg
						backgroundImage: !step.BackgroundColor
							? (t) =>
									`linear-gradient(0deg, ${t.palette.action.hover} 0%, ${t.palette.action.hover} 100%)`
							: "none",

						// left accent bar
						"&::before": (t) => ({
							content: '""',
							position: "absolute",
							left: 8,
							top: 8,
							bottom: 8,
							width: 4,
							borderRadius: 2,
							background:
								t.palette.mode === "light"
									? t.palette.info.main
									: t.palette.info.light,
							boxShadow:
								t.palette.mode === "light"
									? "0 0 0 2px rgba(0,0,0,.06)"
									: "0 0 0 2px rgba(255,255,255,.06)",
						}),
						// add a bit of breathing room from neighbors when highlighted
						marginTop: 2,
						marginBottom: 2,
					}),

					// nice hover for all rows (doesn’t fight highlight)
					"&:hover": (t) => ({
						boxShadow: isHighlighted
							? `
        inset 0 0 0 2px ${t.palette.info.main},
        0 10px 26px ${
					t.palette.mode === "light"
						? "rgba(0,133,255,.24)"
						: "rgba(0,133,255,.34)"
				}`
							: "inset 0 0 0 1px rgba(0,0,0,.10)",
					}),
				}}
				ref={stepRef}
			>
				{(step.IsSkipped || step.IsOverridden) && (
					<Ribbon
						translationString={
							step.IsSkipped
								? "Checklist.Step.Badge.Skipped"
								: "Checklist.Step.Badge.Overridden"
						}
						ribbonText={step.IsSkipped ? "Skipped" : "Overridden"}
						ribbonColor={step.IsSkipped ? "grey" : "darkorange"}
					/>
				)}

				<div style={{ display: "flex", width: "100%" }}>
					<Box
						className={getColumnTypeClass(step)}
						flexDirection={checklist?.ImportedChecklist ? "row" : "column"}
					>
						<StepContent {...props} />
					</Box>

					{showCommentsAndSuggestion && (
						<StepTools
							checklistId={activeSchemeId || 0}
							toggleShowMeasurements={toggleShowMeasurements}
							toggleShowStepLog={toggleShowStepLog}
							step={step}
							skipStep={handleSkipStep}
							liveButtonStyle={stepStatuses.buttonStyling}
						/>
					)}
				</div>

				{measurementsVisible && (
					<StepMeasurements
						activeSchemeId={activeSchemeId || 0}
						autoMode={autoMode}
						listenerTags={step?.ListenerTags ?? []}
						showMeasurements={showMeasurements}
						step={step}
						SetShowMeasurements={setShowMeasurements}
						liveTagStatuses={stepStatuses.liveTagStatuses}
						values={values}
					/>
				)}

				<Box
					sx={{
						display: "flex",
						flexDirection: { xs: "column", lg: "row" },
						marginLeft: "3.5%",
					}}
				>
					<Box sx={{ flex: "0 2 50%" }}>
						{!!props.step.ProcedureUpdateSuggestions.length && (
							<>
								{!showStepLog &&
									props.showCommentsAndSuggestion &&
									props.step.ProcedureUpdateSuggestions.filter(
										(el) => el.Status !== "Declined" && el.Status !== "Closed",
									)
										.slice(0, showAllItems ? undefined : 1)
										.map((suggestion) => (
											<ProcedureUpdateSuggestion
												key={`${suggestion.Date}-${suggestion.Value}`}
												activeSchemeId={props.activeSchemeId || 0}
												suggestion={suggestion}
												step={props.step}
											/>
										))}
							</>
						)}
					</Box>

					<Box sx={{ flex: "0 2 50%" }}>
						{!showStepLog &&
							props.showCommentsAndSuggestion &&
							props.step.Comments?.slice(0, showAllItems ? undefined : 1).map(
								(comment) => (
									<StepComment
										key={`${comment.Date}-${comment.Value}`}
										activeSchemeId={props.activeSchemeId || 0}
										comment={comment}
										step={props.step}
									/>
								),
							)}
					</Box>
				</Box>

				{isEligibleForMoreItems &&
					!showStepLog &&
					props.showCommentsAndSuggestion &&
					(!!props.step.Comments.length ||
						!!props.step.ProcedureUpdateSuggestions.length) && (
						<Box
							sx={{
								width: "100%",
								pr: 2.5,
								py: 1.5,
								display: "flex",
								justifyContent: "flex-end",
								alignItems: "center",
							}}
						>
							<Tooltip title={showAllItems ? "Show less" : "See more"} arrow>
								<IconButton
									onClick={toggleShowAllItems}
									color="info"
									size="medium"
									sx={{
										width: 44,
										height: 44,
										borderRadius: 2,
										border: "1px solid",
										borderColor: "divider",
										bgcolor: "background.paper",
										boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
										transition:
											"transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease",
										"&:hover": {
											transform: "translateY(-2px)",
											boxShadow: "0 12px 30px rgba(0,0,0,0.14)",
											bgcolor: "action.hover",
										},
										"&:active": {
											transform: "translateY(0)",
											boxShadow: "0 6px 16px rgba(0,0,0,0.10)",
										},
									}}
								>
									<ExpandMoreIcon
										sx={{
											transition: "transform 0.2s ease",
											transform: showAllItems ? "rotate(180deg)" : "none",
										}}
									/>
								</IconButton>
							</Tooltip>
						</Box>
					)}
			</Box>

			{showStepLog && (
				<StepLog
					{...props}
					showStepLog={showStepLog}
					toggleShowStepLog={toggleShowStepLog}
				/>
			)}
		</>
	);
};
